import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ManageBranchService } from "../api/services";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
const Report = () => {
   const navigate = useNavigate();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeTab, setActiveTab] = useState("daily"); // daily | monthly | yearly

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailyData, setDailyData] = useState([]);

  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState([]);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearlyData, setYearlyData] = useState([]);

  const [loading, setLoading] = useState(false);

  // ---------- Fetch Daily Transactions ----------
  const fetchDailyData = async (date) => {
    try {
      setLoading(true);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");

      const payload = {
        mode: "date",
        time: `${year}-${month}-${day}`,
      };

      const res = await ManageBranchService.GetcallTransactions(payload);

      if (res.data && res.data.code === 200) {
        const results = res.data.results || [];
        const formatted = results.map((item) => ({
          hour: item.date, 
          count: item.calls_count,
          units: item.calls_charges,
        }));
        setDailyData(formatted);
      } else {
        setDailyData([]);
      }
    } catch (error) {
      console.error("Failed to fetch daily transactions:", error);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Fetch Monthly Transactions ----------
  const fetchMonthlyData = async (date) => {
    try {
      setLoading(true);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");

      const payload = {
        mode: "month",
        time: `${month}-${year}`,
      };

      const res = await ManageBranchService.GetcallTransactions(payload);

      if (res.data && res.data.code === 200) {
        const results = res.data.results || [];
        const formatted = results.map((item) => ({
          date: item.date,
          count: item.calls_count,
          units: item.calls_charges,
        }));
        setMonthlyData(formatted);
      } else {
        setMonthlyData([]);
      }
    } catch (error) {
      console.error("Failed to fetch monthly transactions:", error);
      setMonthlyData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Fetch Yearly Transactions ----------
  const fetchYearlyData = async (year) => {
    try {
      setLoading(true);
      const payload = {
        mode: "year",
        time: year.toString(),
      };

      const res = await ManageBranchService.GetcallTransactions(payload);

      if (res.data && res.data.code === 200) {
        const results = res.data.results || [];
        const formatted = results.map((item) => ({
          month: item.date,
          count: item.calls_count,
          units: item.calls_charges,
        }));
        setYearlyData(formatted);
      } else {
        setYearlyData([]);
      }
    } catch (error) {
      console.error("Failed to fetch yearly transactions:", error);
      setYearlyData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---- Init ----
  useEffect(() => {
    if (activeTab === "daily") {
      fetchDailyData(selectedDate);
    } else if (activeTab === "monthly") {
      fetchMonthlyData(selectedMonth);
    } else if (activeTab === "yearly") {
      fetchYearlyData(selectedYear);
    }
  }, [activeTab, selectedDate, selectedMonth, selectedYear]);

  // ---- Totals ----
  const totalDayCount = dailyData.reduce((sum, item) => sum + item.count, 0);
  const totalDayUnits = dailyData.reduce((sum, item) => sum + item.units, 0);

  const totalMonthCount = monthlyData.reduce((sum, item) => sum + item.count, 0);
  const totalMonthUnits = monthlyData.reduce((sum, item) => sum + item.units, 0);

  const totalYearCount = yearlyData.reduce((sum, item) => sum + item.count, 0);
  const totalYearUnits = yearlyData.reduce((sum, item) => sum + item.units, 0);

  // ---- Change Date ----
  const changeDay = (direction) => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + direction);
      return newDate;
    });
  };

  const changeMonth = (direction) => {
    setSelectedMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const changeYear = (direction) => {
    setSelectedYear((prev) => prev + direction);
  };

  // Formatters
  const formatMonthYear = (date) =>
    date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const formatDate = (date) =>
    date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="container mt-3">
      <button
      className="btn btn-outline-secondary mb-3"
       onClick={() => navigate(-1)}>
        ← Back
    </button>
      {/* Tabs */}
      <div className="d-flex mb-3">
        <button
          className={`flex-fill btn ${activeTab === "daily" ? "CommonColor" : "btn-light"}`}
          onClick={() => setActiveTab("daily")}
        >
          Daily
        </button>
        <button
          className={`flex-fill btn ${activeTab === "monthly" ? "CommonColor" : "btn-light"}`}
          onClick={() => setActiveTab("monthly")}
        >
          Monthly
        </button>
        <button
          className={`flex-fill btn ${activeTab === "yearly" ? "CommonColor" : "btn-light"}`}
          onClick={() => setActiveTab("yearly")}
        >
          Yearly
        </button>
      </div>

      {/* -------- DATE PICKER CONTROL -------- */}
      <div className="d-flex justify-content-center align-items-center mb-3 position-relative">
        <button
          className="btn btn-outline-warning rounded-circle mx-2 CommonColor text-white"
          onClick={() =>
            activeTab === "daily"
              ? changeDay(-1)
              : activeTab === "monthly"
              ? changeMonth(-1)
              : changeYear(-1)
          }
        >
          <FaArrowLeft />
        </button>

        <span
          className="fw-bold text-dark"
          style={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={() => setShowDatePicker((prev) => !prev)}
        >
          {activeTab === "daily"
            ? formatDate(selectedDate)
            : activeTab === "monthly"
            ? formatMonthYear(selectedMonth)
            : selectedYear}
        </span>

        <button
          className="btn btn-outline-warning rounded-circle mx-2 CommonColor text-white"
          onClick={() =>
            activeTab === "daily"
              ? changeDay(1)
              : activeTab === "monthly"
              ? changeMonth(1)
              : changeYear(1)
          }
        >
          <FaArrowRight />
        </button>

        {/* Inline DatePicker */}
        {showDatePicker && (
          <div style={{ position: "absolute", top: "40px", zIndex: 1000 }}>
            <DatePicker
              selected={
                activeTab === "daily"
                  ? selectedDate
                  : activeTab === "monthly"
                  ? selectedMonth
                  : new Date(selectedYear, 0)
              }
              onChange={(date) => {
                if (activeTab === "daily") setSelectedDate(date);
                else if (activeTab === "monthly") setSelectedMonth(date);
                else setSelectedYear(date.getFullYear());

                setShowDatePicker(false);
              }}
              inline
              showMonthYearPicker={activeTab === "monthly"}
              showYearPicker={activeTab === "yearly"}
              dateFormat={
                activeTab === "daily"
                  ? "dd/MM/yyyy"
                  : activeTab === "monthly"
                  ? "MM/yyyy"
                  : "yyyy"
              }
            />
          </div>
        )}
      </div>

      {/* -------- DAILY TAB -------- */}
      {activeTab === "daily" && (
        <>
          {/* Chart */}
          <div className="border rounded p-3 mb-3 bg-white">
            {loading ? (
              <div className="text-center my-5">
                <div className="loader">
                  <div className="lds-spinner">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i}></div>
                    ))}
                  </div>
                </div>
              </div>
            )  : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#ff7300" />
                  <Line type="monotone" dataKey="units" stroke="#ff0000" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Daily Summary */}
          <table className="table table-bordered text-center mb-3">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Total Count</th>
                <th>Total Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{formatDate(selectedDate)}</td>
                <td className="text-danger fw-bold">{totalDayCount}</td>
                <td className="text-danger fw-bold">{totalDayUnits}</td>
              </tr>
            </tbody>
          </table>

          {/* Hourly Breakdown */}
          <table className="table table-bordered text-center">
            <thead className="table-light">
              <tr>
                <th>Hour</th>
                <th>Count</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              {dailyData.length > 0 ? (
                dailyData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.hour}</td>
                    <td className="text-danger">{item.count}</td>
                    <td className="text-danger">{item.units}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">No data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {/* -------- MONTHLY TAB -------- */}
      {activeTab === "monthly" && (
        <>
          {/* Chart */}
          <div className="border rounded p-3 mb-3 bg-white">
           {loading ? (
                <div className="text-center my-5">
                  <div className="loader">
                    <div className="lds-spinner">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i}></div>
                      ))}
                    </div>
                  </div>
                </div>
              )  : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#ff7300" />
                  <Line type="monotone" dataKey="units" stroke="#ff0000" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Month Summary */}
          <table className="table table-bordered text-center mb-3">
            <thead className="table-light">
              <tr>
                <th>Month</th>
                <th>Total Count</th>
                <th>Total Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{formatMonthYear(selectedMonth)}</td>
                <td className="text-danger fw-bold">{totalMonthCount}</td>
                <td className="text-danger fw-bold">{totalMonthUnits}</td>
              </tr>
            </tbody>
          </table>

          {/* Daily Breakdown */}
          <table className="table table-bordered text-center">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Count</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.length > 0 ? (
                monthlyData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.date}</td>
                    <td className="text-danger">{item.count}</td>
                    <td className="text-danger">{item.units}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">No data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {/* -------- YEARLY TAB -------- */}
      {activeTab === "yearly" && (
        <>
          {/* Chart */}
          <div className="border rounded p-3 mb-3 bg-white">
            {loading ? (
              <div className="text-center my-5">
                <div className="loader">
                  <div className="lds-spinner">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i}></div>
                    ))}
                  </div>
                </div>
              </div>
            )  : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={yearlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#ff7300" />
                  <Line type="monotone" dataKey="units" stroke="#ff0000" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Year Summary */}
          <table className="table table-bordered text-center mb-3">
            <thead className="table-light">
              <tr>
                <th>Year</th>
                <th>Total Count</th>
                <th>Total Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{selectedYear}</td>
                <td className="text-danger fw-bold">{totalYearCount}</td>
                <td className="text-danger fw-bold">{totalYearUnits}</td>
              </tr>
            </tbody>
          </table>

          {/* Monthly Breakdown */}
          <table className="table table-bordered text-center">
            <thead className="table-light">
              <tr>
                <th>Month</th>
                <th>Count</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.length > 0 ? (
                yearlyData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.month}</td>
                    <td className="text-danger">{item.count}</td>
                    <td className="text-danger">{item.units}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3">No data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default Report;
